import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { formatYAxis } from './analysisCompareUtils';
import {
  buildTypeSnapshotAtYear,
  buildTypeTimeSeries,
  getAssetTypeLabel,
  getTypesFromBundle,
  pickDefaultAssetType,
} from './analysisAssetTypes';

const AssetsByTypeChart = ({
  projection = [],
  bundle = null,
  yearIndex = 0,
  formatCurrency,
}) => {
  const types = getTypesFromBundle(bundle);
  const includeUnlinked = bundle?.hasUnlinkedDebt;

  const [selectedType, setSelectedType] = useState(() => pickDefaultAssetType(types));

  useEffect(() => {
    if (!types.length) {
      setSelectedType(null);
      return;
    }
    if (!selectedType || !types.includes(selectedType)) {
      setSelectedType(pickDefaultAssetType(types));
    }
  }, [types, selectedType]);

  const snapshot = useMemo(() => {
    const row = projection[yearIndex] || projection[projection.length - 1] || {};
    return buildTypeSnapshotAtYear(row, types, includeUnlinked);
  }, [projection, yearIndex, types, includeUnlinked]);

  const row = projection[yearIndex] || projection[projection.length - 1] || {};

  const timeSeries = useMemo(
    () => (selectedType ? buildTypeTimeSeries(projection, selectedType) : []),
    [projection, selectedType],
  );

  if (!types.length) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        Ajoutez des actifs au bilan pour voir la répartition par type.
      </p>
    );
  }

  const typeLabel = selectedType ? getAssetTypeLabel(selectedType) : '';
  const barHeight = Math.max(220, snapshot.length * 40);

  return (
    <div className="space-y-6" data-testid="assets-by-type-chart">
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-2">
          Évolution par type
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          {types.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setSelectedType(type)}
              className={`rounded-lg border px-2.5 py-1.5 text-xs transition-colors ${
                selectedType === type
                  ? 'border-primary bg-primary/10 text-foreground font-medium'
                  : 'border-border text-muted-foreground hover:border-primary/40'
              }`}
            >
              {getAssetTypeLabel(type)}
            </button>
          ))}
        </div>
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timeSeries} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
              <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} width={56} />
              <Tooltip formatter={(v, name) => [formatCurrency(v), name]} />
              <Legend wrapperStyle={{ fontSize: '0.7rem' }} />
              <Line type="monotone" dataKey="assets" name="Valeur de rachat" stroke="#22c55e" strokeWidth={2.5} dot={false} />
              {selectedType === 'assurance_participative' && (
                <>
                  <Line type="monotone" dataKey="deathBenefit" name="Capital décès" stroke="#0ea5e9" strokeWidth={2} dot={false} strokeDasharray="4 2" />
                  <Line type="monotone" dataKey="encumbranceAvailable" name="Capacité collatérale" stroke="#a855f7" strokeWidth={1.5} dot={false} strokeDasharray="2 2" />
                </>
              )}
              <Line type="monotone" dataKey="debts" name="Passifs liés" stroke="#ef4444" strokeWidth={2} dot={false} strokeDasharray="5 3" />
              <Line type="monotone" dataKey="net" name="Valeur nette (rachat − dettes)" stroke="#3b82f6" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-center">
          {selectedType === 'assurance_participative'
            ? `${typeLabel} — valeur de rachat dans les actifs totaux; capital décès affiché à titre informatif (non additionné à la valeur nette).`
            : `${typeLabel} — passifs = dettes liées aux actifs de ce type (ex. hypothèque sur immeuble).`}
        </p>
      </div>

      <div>
        <p className="text-xs font-medium text-muted-foreground mb-2">
          Vue d&apos;ensemble — actifs vs passifs par catégorie (année {row.year ?? '—'})
        </p>
        <div style={{ height: barHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={snapshot}
              layout="vertical"
              margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis type="number" tickFormatter={formatYAxis} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={140} />
              <Tooltip formatter={(v, name) => [formatCurrency(v), name]} />
              <Legend wrapperStyle={{ fontSize: '0.65rem' }} />
              <Bar dataKey="assets" name="Valeur de rachat / actifs" fill="#22c55e" radius={[0, 4, 4, 0]} />
              <Bar dataKey="deathBenefit" name="Capital décès" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
              <Bar dataKey="debts" name="Passifs liés" fill="#ef4444" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AssetsByTypeChart;
